import { z } from 'zod';
import { idSchema } from './common.js';
// -------- Vehicles --------
export const vehicleTypeSchema = z.enum([
    'van',
    'rigid_7_5t',
    'rigid_18t',
    'rigid_26t',
    'artic',
    'drawbar',
    'flatbed',
    'curtainsider',
    'box',
    'refrigerated',
    'tipper',
    'other',
    'cycle',
    'cargo_bike',
    'motorcycle',
    'car',
    'small_van',
    'midi_van',
    'swb',
    'mwb',
    'lwb',
    'xlwb',
    'luton',
    'jumbo_luton',
    'rigid_12t',
    'skellie_1x20',
    'skellie_2x20',
    'skellie_1x40',
    'skellie_1x44',
    'tractor_4axle',
    'tractor_6axle',
]);
/**
 * UK VRM — we accept a lot. The Phase 1 decision was "trust either" — so any
 * 1–12 alphanumeric string is accepted. Stored uppercase, spaces stripped.
 */
const registrationSchema = z
    .string()
    .trim()
    .transform((s) => s.toUpperCase().replace(/\s+/g, ''))
    .pipe(z
    .string()
    .min(1, 'Registration required')
    .max(12, 'Registration too long')
    .regex(/^[A-Z0-9]+$/, 'Registration must be alphanumeric'));
export const createVehicleBodySchema = z.object({
    registration: registrationSchema,
    type: vehicleTypeSchema,
    /** 0049: null/absent = fleet pool; set = owned by that driver. */
    ownerDriverUserId: idSchema.nullable().optional(),
    fleetNumber: z.string().trim().max(20).nullable().optional(),
    make: z.string().trim().max(40).nullable().optional(),
    model: z.string().trim().max(60).nullable().optional(),
    year: z.number().int().min(1970).max(2100).nullable().optional(),
    colour: z.string().trim().max(20).nullable().optional(),
    grossWeightKg: z.number().int().min(0).max(100_000).nullable().optional(),
    payloadKg: z.number().int().min(0).max(100_000).nullable().optional(),
    lengthMm: z.number().int().min(0).max(25_000).nullable().optional(),
    heightMm: z.number().int().min(0).max(10_000).nullable().optional(),
    widthMm: z.number().int().min(0).max(5_000).nullable().optional(),
    fuelType: z.string().trim().max(20).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
});
export const updateVehicleBodySchema = z
    .object({
    registration: registrationSchema.optional(),
    type: vehicleTypeSchema.optional(),
    ownerDriverUserId: idSchema.nullable().optional(),
    fleetNumber: z.string().trim().max(20).nullable().optional(),
    make: z.string().trim().max(40).nullable().optional(),
    model: z.string().trim().max(60).nullable().optional(),
    year: z.number().int().min(1970).max(2100).nullable().optional(),
    colour: z.string().trim().max(20).nullable().optional(),
    grossWeightKg: z.number().int().min(0).max(100_000).nullable().optional(),
    payloadKg: z.number().int().min(0).max(100_000).nullable().optional(),
    lengthMm: z.number().int().min(0).max(25_000).nullable().optional(),
    heightMm: z.number().int().min(0).max(10_000).nullable().optional(),
    widthMm: z.number().int().min(0).max(5_000).nullable().optional(),
    fuelType: z.string().trim().max(20).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    isActive: z.boolean().optional(),
})
    .strict();
export const vehicleIdParamSchema = z.object({
    fleetId: idSchema,
    vehicleId: idSchema,
});
// -------- Vehicle documents --------
export const vehicleDocumentTypeSchema = z.enum([
    'mot',
    'ved',
    'insurance',
    'o_licence_disc',
    'ppv',
    'loler',
    'tacho_calibration',
    'service_history',
    'other',
]);
/** YYYY-MM-DD (calendar date). Pipe to JS Date for sanity-check, then emit back as string. */
export const calendarDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
    .refine((s) => {
    const d = new Date(s);
    return !Number.isNaN(d.getTime());
}, 'Invalid calendar date');
export const createVehicleDocumentBodySchema = z
    .object({
    type: vehicleDocumentTypeSchema,
    label: z.string().trim().max(80).nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    issuedAt: calendarDateSchema.nullable().optional(),
    expiresAt: calendarDateSchema.nullable().optional(),
    fileKey: z.string().trim().max(500).nullable().optional(),
    fileName: z.string().trim().max(200).nullable().optional(),
    fileSizeBytes: z.number().int().min(0).nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.type === 'other' && !v.label) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['label'],
            message: 'label is required when type is "other"',
        });
    }
});
export const updateVehicleDocumentBodySchema = z
    .object({
    type: vehicleDocumentTypeSchema.optional(),
    label: z.string().trim().max(80).nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    issuedAt: calendarDateSchema.nullable().optional(),
    expiresAt: calendarDateSchema.nullable().optional(),
    fileKey: z.string().trim().max(500).nullable().optional(),
    fileName: z.string().trim().max(200).nullable().optional(),
    fileSizeBytes: z.number().int().min(0).nullable().optional(),
})
    .strict();
export const vehicleDocumentIdParamSchema = z.object({
    fleetId: idSchema,
    vehicleId: idSchema,
    docId: idSchema,
});
// -------- Driver documents (me-side) --------
export const driverDocumentTypeSchema = z.enum([
    'licence_card',
    'licence_check_code',
    'cpc_card',
    'digicard',
    'medical',
    'dbs',
    'adr',
    'hiab',
    'passport',
    'other',
]);
export const createDriverDocumentBodySchema = z
    .object({
    type: driverDocumentTypeSchema,
    label: z.string().trim().max(80).nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    issuedAt: calendarDateSchema.nullable().optional(),
    expiresAt: calendarDateSchema.nullable().optional(),
    fileKey: z.string().trim().max(500).nullable().optional(),
    fileName: z.string().trim().max(200).nullable().optional(),
    fileSizeBytes: z.number().int().min(0).nullable().optional(),
    visibleToFleetIds: z.array(idSchema).max(100).optional(),
})
    .superRefine((v, ctx) => {
    if (v.type === 'other' && !v.label) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['label'],
            message: 'label is required when type is "other"',
        });
    }
});
export const updateDriverDocumentBodySchema = z
    .object({
    type: driverDocumentTypeSchema.optional(),
    label: z.string().trim().max(80).nullable().optional(),
    reference: z.string().trim().max(80).nullable().optional(),
    issuedAt: calendarDateSchema.nullable().optional(),
    expiresAt: calendarDateSchema.nullable().optional(),
    fileKey: z.string().trim().max(500).nullable().optional(),
    fileName: z.string().trim().max(200).nullable().optional(),
    fileSizeBytes: z.number().int().min(0).nullable().optional(),
    visibleToFleetIds: z.array(idSchema).max(100).optional(),
})
    .strict();
export const driverDocumentIdParamSchema = z.object({
    docId: idSchema,
});
// -------- Fleet-side driver view --------
export const driverMemberIdParamSchema = z.object({
    fleetId: idSchema,
    memberId: idSchema,
});
// -------- Vehicle verification (S76) --------
export const verificationRecheckQuerySchema = z.object({
    checkType: z.enum(['dvla_ves', 'dvsa_mot_history']).optional(),
});
//# sourceMappingURL=vehicle.js.map